/**
 * AktenProzessSpalte — Zweite Spalte mit Prozessschritten (WinValue-Style)
 * 
 * v5.3 DESIGN-UPDATE:
 * - HELLES Design (bg-white/bg-gray-50) statt dunkles Schwarz
 * - Farbiger Trennbalken (3-4px) links in der Betriebsfarbe
 * - Dunkler Text auf hellem Hintergrund
 * - Aktiver Schritt: Betriebsfarbe als Hintergrund mit weißem Text
 *
 * v5.4: Prozessschritte passen sich dem Auftragstyp an — bei
 * Sachverständigenverfahren (akte.auftragstyp_sv) wird eine eigene
 * Schrittfolge gezeigt (siehe getProzessNav in lib/navigation).
 *
 * v5.5: Zusätzlicher Reiter „SV-Verfahren" — erscheint NUR, wenn für die Akte
 * ein sv_verfahren-Datensatz existiert. Klick öffnet das SV-Cockpit
 * (SachverstaendigenverfahrenTab) als Panel über der Detailansicht
 * (Fortschritt, Stepper, Phasen-Detail, Timeline, Wissensbasis, Entwürfe).
 */

import { useEffect, useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { useAkteByNr } from '@/hooks/useSupabase';
import { getProzessNav, type AktenProzessItem } from '@/lib/navigation';
import { getBetriebColor } from '@/lib/betriebe';
import { supabase } from '@/lib/supabase';
import SachverstaendigenverfahrenTab from '@/components/akte/SachverstaendigenverfahrenTab';
import { useLocation } from 'wouter';
import {
  ArrowLeft, FileText, ClipboardList, User, Car, Eye, Search, BarChart3,
  Wrench, TrendingDown, ArrowDownRight, FileCheck, Receipt, Files, ScrollText,
  CheckCircle2, Circle, AlertCircle, Clock, FolderOpen, PenTool, Camera,
  Layers, MessageSquare, ArrowRightLeft, AlertTriangle, UserPlus, Phone,
  Settings, CheckCircle, LogIn, Stethoscope, Calculator, Package, LogOut,
  Shield, Sparkles, ShoppingCart, Truck, MapPin, RotateCcw, CalendarCheck,
  Scale, Gavel, Handshake, X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

const ICON_MAP: Record<string, LucideIcon> = {
  FileText, ClipboardList, User, Car, Eye, Search, BarChart3,
  Wrench, TrendingDown, ArrowDownRight, FileCheck, Receipt, Files, ScrollText,
  FolderOpen, PenTool, Camera, Layers, MessageSquare, ArrowRightLeft, AlertTriangle,
  UserPlus, Phone, Settings, CheckCircle, LogIn, Stethoscope, Calculator,
  Package, LogOut, Shield, Sparkles, ShoppingCart, Truck, MapPin, RotateCcw,
  CalendarCheck, Scale, Gavel, Handshake,
};

interface ProcessStepStatus {
  status: 'fertig' | 'inBearbeitung' | 'offen' | 'fehler';
}

/**
 * Compute process step status dynamically from akte data.
 * A step is 'fertig' if its key fields are populated, 'inBearbeitung' if partially filled, 'offen' otherwise.
 */
function computeProcessStatus(akte: Record<string, any> | null): Record<string, ProcessStepStatus> {
  if (!akte) return {};
  const has = (...keys: string[]) => keys.some(k => akte[k] != null && akte[k] !== '');
  const allSet = (...keys: string[]) => keys.every(k => akte[k] != null && akte[k] !== '');

  const step = (filled: boolean, partial = false): ProcessStepStatus => ({
    status: filled ? 'fertig' : partial ? 'inBearbeitung' : 'offen',
  });

  return {
    uebersicht: step(true), // always accessible
    auftrag: step(
      allSet('schadenart', 'auftragsdatum'),
      has('schadenart', 'auftragsdatum', 'schadennummer'),
    ),
    beteiligte: step(
      has('anspruchsteller_name', 'versicherung'),
      has('anspruchsteller_name', 'versicherung', 'gegner_name', 'anwalt_name', 'werkstatt_name'),
    ),
    unterschrift: step(has('unterschrift_datum')),
    fahrzeug: step(
      allSet('fahrzeug_marke', 'kennzeichen'),
      has('fahrzeug_marke', 'kennzeichen', 'fahrzeug_vin'),
    ),
    besichtigungen: step(has('besichtigungsdatum')),
    begutachtung: step(has('begutachtung_abgeschlossen')),
    lichtbilder: step(has('winvalue_documents_count') && Number(akte.winvalue_documents_count) > 0),
    instandsetzung: step(has('reparaturkosten_netto')),
    bewertung: step(
      allSet('reparaturkosten_brutto', 'wiederbeschaffungswert'),
      has('reparaturkosten_brutto', 'wiederbeschaffungswert'),
    ),
    restwert: step(has('restwert')),
    minderwert: step(has('minderwert')),
    beurteilung: step(has('beurteilung_abgeschlossen')),
    gutachten: step(has('gutachten_fertig')),
    rechnung: (() => {
      if (has('rechnung_bezahlt') && akte.rechnung_bezahlt) return { status: 'fertig' as const };
      if (has('kuerzung_betrag') && Number(akte.kuerzung_betrag) > 0) return { status: 'fehler' as const };
      if (has('gesamtbetrag')) return { status: 'inBearbeitung' as const };
      return { status: 'offen' as const };
    })(),
    dokumente: step(has('winvalue_documents_count') && Number(akte.winvalue_documents_count) > 0),
    protokoll: step(akte.status === 'abgeschlossen'),
  };
}

function getStatusIcon(status: string): LucideIcon {
  switch (status) {
    case 'fertig': return CheckCircle2;
    case 'inBearbeitung': return Clock;
    case 'fehler': return AlertCircle;
    default: return Circle;
  }
}

function getStatusColor(status: string, isActive: boolean): string {
  if (isActive) return 'text-white';
  switch (status) {
    case 'fertig': return 'text-green-500';
    case 'inBearbeitung': return 'text-amber-500';
    case 'fehler': return 'text-red-500';
    default: return 'text-gray-400';
  }
}

interface AktenProzessSpalteProps {
  mobile?: boolean;
  akte?: Record<string, any> | null;
}

export default function AktenProzessSpalte({ mobile, akte: akteProp }: AktenProzessSpalteProps) {
  const { activeAkte, closeAkte } = useApp();
  const [location, setLocation] = useLocation();
  const { data: fetchedAkte } = useAkteByNr(activeAkte?.aktenNr);

  // SV-Cockpit: Reiter erscheint nur bei vorhandenem sv_verfahren-Datensatz.
  const akteForSv = akteProp ?? fetchedAkte;
  const [svExists, setSvExists] = useState(false);
  const [svOpen, setSvOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const aId = (akteForSv as any)?.id;
    if (!aId) { setSvExists(false); return; }
    (async () => {
      try {
        const { data } = await supabase
          .from('sv_verfahren')
          .select('id')
          .eq('akte_id', aId)
          .limit(1)
          .maybeSingle();
        if (!cancelled) setSvExists(Boolean(data));
      } catch {
        if (!cancelled) setSvExists(false);
      }
    })();
    return () => { cancelled = true; };
  }, [(akteForSv as any)?.id]);

  if (!activeAkte) return null;

  const akteData = akteProp ?? fetchedAkte;
  const section = location.split('/').pop() || 'uebersicht';
  const betriebColor = getBetriebColor(activeAkte.businessUnit);
  // Navi passt sich dem Auftragstyp an (z. B. Sachverständigenverfahren).
  const baseSchritte = getProzessNav(activeAkte.businessUnit, akteData?.auftragstyp_sv);
  const SV_ITEM = { id: 'svverfahren', label: 'SV-Verfahren', icon: 'Gavel', path: '/svverfahren', status: 'aktiv' } as AktenProzessItem;
  const prozessSchritte: AktenProzessItem[] =
    svExists && !baseSchritte.some((s) => s.id === 'svverfahren')
      ? [...baseSchritte, SV_ITEM]
      : baseSchritte;
  const processStatus = computeProcessStatus(akteData);

  const handleStep = (item: AktenProzessItem, sectionId: string) => {
    if (item.id === 'svverfahren') { setSvOpen(true); return; }
    setLocation(`/akten/${activeAkte.businessUnit}/${activeAkte.aktenNr}/${sectionId}`);
  };

  // SV-Cockpit-Overlay (in beiden Layouts verfügbar)
  const svOverlay = svOpen && akteData ? (
    <div
      className="fixed inset-0 z-[140] bg-black/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-3 sm:p-6"
      onClick={() => setSvOpen(false)}
      data-testid="sv-cockpit-overlay"
    >
      <div className="bg-gray-50 rounded-2xl shadow-2xl w-full max-w-3xl my-2 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-4 py-3 bg-white border-b border-gray-200 sticky top-0 z-10">
          <Gavel className="w-4 h-4" style={{ color: '#C41E3A' }} />
          <span className="text-sm font-semibold text-gray-800 flex-1 truncate">
            Sachverständigenverfahren · {activeAkte.aktenNr}
          </span>
          <button onClick={() => setSvOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="p-4">
          <SachverstaendigenverfahrenTab akte={akteData as Record<string, any>} />
        </div>
      </div>
    </div>
  ) : null;

  // ===== MOBILE: Horizontale scrollbare Leiste =====
  if (mobile) {
    return (
      <>
      <div className="flex items-center gap-1 px-3 py-2 overflow-x-auto scrollbar-hide bg-gray-50 border-b border-gray-200">
        {/* Zurück-Button */}
        <button
          onClick={() => {
            closeAkte();
            setLocation(`/akten/${activeAkte.businessUnit}`);
          }}
          className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-gray-200 hover:bg-gray-300 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 text-gray-600" />
        </button>

        {/* Aktenzeichen */}
        <span className="flex-shrink-0 text-[10px] font-mono font-bold text-gray-500 px-2">
          {activeAkte.aktenNr}
        </span>

        {/* Divider */}
        <div className="flex-shrink-0 w-px h-5 bg-gray-300" />

        {/* Prozessschritte horizontal */}
        {prozessSchritte.map((item: AktenProzessItem) => {
          const sectionId = item.path.replace(/^\//, '');
          const isActive = item.id === 'svverfahren' ? svOpen : section === sectionId;
          const stepStatus = processStatus[sectionId];
          const StatusIcon = getStatusIcon(stepStatus?.status || 'offen');
          const statusColor = getStatusColor(stepStatus?.status || 'offen', isActive);

          return (
            <button
              key={item.id}
              onClick={() => handleStep(item, sectionId)}
              className={`flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${
                isActive
                  ? 'text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
              }`}
              style={isActive ? { backgroundColor: betriebColor.accent } : undefined}
            >
              <StatusIcon className={`w-3 h-3 ${statusColor}`} />
              {item.label}
            </button>
          );
        })}
      </div>
      {svOverlay}
      </>
    );
  }

  // ===== DESKTOP: Vertikale Spalte (220px) — HELLES DESIGN =====
  return (
    <>
    <div className="flex flex-col h-full bg-gray-50">
      {/* Farbiger Trennbalken oben (4px) in Betriebsfarbe */}
      <div
        className="h-1 w-full flex-shrink-0"
        style={{
          background: `linear-gradient(90deg, ${betriebColor.from}, ${betriebColor.to})`,
        }}
      />

      {/* Header: Zurück + Aktenzeichen + Kundenname */}
      <div className="px-3 py-3 border-b border-gray-200 flex-shrink-0 bg-white">
        <button
          onClick={() => {
            closeAkte();
            setLocation(`/akten/${activeAkte.businessUnit}`);
          }}
          className="flex items-center gap-2 w-full rounded-lg px-2 py-1.5 hover:bg-gray-100 active:bg-gray-200 transition-colors group mb-2"
        >
          <ArrowLeft className="w-4 h-4 text-gray-400 group-hover:text-gray-600 flex-shrink-0" />
          <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 group-hover:text-gray-600">
            Zurück zur Liste
          </span>
        </button>

        <div className="px-2">
          <span className="text-[13px] font-mono font-bold text-gray-800 block truncate">
            {activeAkte.aktenNr}
          </span>
          <span className="text-[11px] text-gray-500 block truncate mt-0.5">
            {activeAkte.kundenName}
          </span>
        </div>
      </div>

      {/* Prozessschritte */}
      <nav className="flex-1 overflow-y-auto py-2 px-2" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(0,0,0,0.1) transparent' }}>
        <div className="px-2 pt-1 pb-2">
          <span className="text-[9px] font-semibold uppercase tracking-widest text-gray-400">
            Prozessschritte
          </span>
        </div>

        {prozessSchritte.map((item: AktenProzessItem) => {
          const Icon = ICON_MAP[item.icon] || FileText;
          const sectionId = item.path.replace(/^\//, '');
          const isActive = item.id === 'svverfahren' ? svOpen : section === sectionId;
          const stepStatus = processStatus[sectionId];
          const StatusIcon = getStatusIcon(stepStatus?.status || 'offen');
          const statusColor = getStatusColor(stepStatus?.status || 'offen', isActive);

          return (
            <button
              key={item.id}
              onClick={() => handleStep(item, sectionId)}
              className={`w-full flex items-center gap-2.5 rounded-lg mb-0.5 px-2.5 py-2 transition-all duration-150 ${
                isActive
                  ? 'text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200 active:bg-gray-300'
              }`}
              style={isActive ? { backgroundColor: betriebColor.accent } : undefined}
              title={item.label}
            >
              {/* Status-Icon links */}
              <StatusIcon className={`w-3.5 h-3.5 flex-shrink-0 ${statusColor}`} />

              {/* Label */}
              <span className="text-[13px] font-medium truncate flex-1 text-left">{item.label}</span>

              {/* SV-Reiter: dezentes Cockpit-Icon rechts */}
              {item.id === 'svverfahren' && (
                <Gavel className={`w-3.5 h-3.5 flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-400'}`} />
              )}
            </button>
          );
        })}
      </nav>
    </div>
    {svOverlay}
    </>
  );
}
